import Vue from 'vue';
import Fingerprint2 from 'fingerprintjs2';
import UAParser from 'ua-parser-js';
import { Resource } from './StorageService';
import { Statement } from '@/helpers/Interfaces';
import { AccountType } from '@/helpers/Enums';
import { DateTime } from 'luxon';

/**
 * AuthService
 */
export default class AuthService
{
    /**
     * @returns Promise<any>
     */
    protected static getFingerprint(): Promise<any>
    {
        return new Promise(resolve =>
        {
            let options: any = {excludes: {fonts: true, canvas: true, webgl: true, audio: true}};

            Fingerprint2.get(options, (components: any) => resolve({
                result: Fingerprint2.x64hash128(components.map((c: any) => c.value).join('~~~'), 31),
                components: components
            }));
        });
    }

    /**
     * @param username string
     * @param password string
     *
     * @returns Promise<TokenModel>
     */
    public static async login(username: string, password: string): Promise<TokenModel>
    {
        let fingerprint = await AuthService.getFingerprint();
        let parser = new UAParser();
        let device = parser.getResult();

        return (await Vue.axios.post<TokenModel>('auth/login', {
            username: username,
            password: password,
            deviceId: fingerprint.result,
            deviceName: `${device.browser.name} on ${device.os.name} ${device.os.version}`
        })).data;
    }

    /**
     * @returns Promise<TokenModel>
     */
    public static async ntlm(): Promise<TokenModel>
    {
        let fingerprint = await AuthService.getFingerprint();
        let parser = new UAParser();
        let device = parser.getResult();

        return (await Vue.axios.post<TokenModel>('auth/ntlm', {
            deviceId: fingerprint.result,
            deviceName: `${device.browser.name} on ${device.os.name} ${device.os.version}`
        }, {
            withCredentials: true
        })).data;
    }

    /**
     * @param refreshToken string
     *
     * @returns Promise<TokenModel>
     */
    public static async refreshToken(): Promise<TokenModel>
    {
        return (await Vue.axios.get<TokenModel>('auth/token/refresh')).data;
    }

    /**
     * @param refreshToken string
     *
     * @returns Promise<TokenModel>
     */
    public static async recoverToken(refreshToken: string): Promise<TokenModel>
    {
        return (await Vue.axios.post<TokenModel>('auth/token/recover', {
            token: refreshToken
        })).data;
    }

    /**
     * @param refreshToken string
     *
     * @returns Promise<Statement>
     */
    public static async revokeToken(refreshToken: string): Promise<Statement>
    {
        return (await Vue.axios.post<Statement>('auth/token/revoke', {
            token: refreshToken
        })).data;
    }

    /**
     * @returns Promise<AuthModel>
     */
    public static async getIdentity(real: boolean = false): Promise<AuthModel>
    {
        return (await Vue.axios.get<AuthModel>('auth/profile', {
            transformRequest: [(data, headers) =>
            {
                if (real == true)
                    delete headers.common.Impersonate;

                return data;
            }]
        })).data;
    }

    /**
     * @param user ProfileModel
     *
     * @returns Promise<Statement>
     */
    public static async updateProfile(user: ProfileModel): Promise<Statement>
    {
        return (await Vue.axios.put<Statement>('auth/profile', user)).data;
    }

    /**
     * @param email string
     * @param callbackUrl string
     *
     * @returns Promise<string>
     */
    public static async resetPassword(email: string, callbackUrl: string): Promise<string>
    {
        return (await Vue.axios.post<string>('auth/reset-password', {
            email: email,
            callbackUrl: callbackUrl
        })).data;
    }

    /**
     * @param token string
     * @param email string
     * @param newPassword string
     * @param repeatPassword string
     *
     * @returns Promise<string>
     */
    public static async setPassword(token: string, email: string, newPassword: string, repeatPassword: string): Promise<string>
    {
        return (await Vue.axios.post<string>('auth/set-password', {
            token: token,
            email: email,
            newPassword: newPassword,
            repeatPassword: repeatPassword
        })).data;
    }

    /**
     * @param model PasswordModel
     *
     * @returns Promise<string>
     */
    public static async changePassword(model: PasswordModel): Promise<string>
    {
        return (await Vue.axios.post<string>('auth/change-password', model)).data;
    }

    /**
     * @returns Promise<number>
     */
    public static async passwordExpires(): Promise<number>
    {
        return (await Vue.axios.get<number>('auth/password-expires')).data;
    }

    /**
     * @returns Promise<Substitution[]>
     */
    public static async getSubstitutions(): Promise<Substitution[]>
    {
        return (await Vue.axios.get<Substitution[]>('auth/substitutions')).data;
    }
}

export interface TokenModel
{
    token: string;
    refresh: string;
    expires: string;
}

export interface AuthModel
{
    id: number;
    userName: string;
    email: string;
    givenName: string;
    surname: string;
    facePicture: Resource;
    permissions: string[];
    forcePasswordChange: boolean;
    accountType: AccountType;
    systemVersion: string;
}

export interface ProfileModel
{
    userName: string;
    email: string;
    givenName: string;
    surname: string;
    facePicture: Resource;
}

export interface LoginModel
{
    username: string;
    password: string;
    rememberMe: boolean;
}

export interface PasswordModel
{
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
}

export interface ResetModel
{
    email: string;
    newPassword: string;
    repeatPassword: string;
}

export interface Substitution
{
    id: number;
    name: string;
    intervals: Interval[];
}

export interface Interval
{
    dateBeginUtc: DateTime;
    dateEndUtc: DateTime;
}